import * as React from 'react';
import { Formik, FormikConsumer } from 'formik';
import User, { AssignedClient, UserOktaStatus } from '../../../../models/user';
import OutlinedInput from '../../../common/outlined-input';
import * as commonStyles from '../../../common/common.module.scss';
import PrimaryButton from '../../../common/primary-button';
import { CircularProgress, makeStyles } from '@material-ui/core';
import * as Yup from 'yup';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router';
import TransferList from '../../../common/transfer-list';
import SwitchBox from '../../../common/switchBox';
import useUser from '../../../../hooks/useUser';
import _ from 'lodash';
import RadioForm from '../../../common/radio-form';
import Client, { AssignedUser } from '../../../../models/client';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import Paths from '../../../../models/enums/paths';
import Application from '../../../../models/application';
import TertiaryButton from '../../../common/tertiary-button';
import { useRef } from 'react';
import useTag from '../../../../hooks/useTag';
import ClientListItem from './clientListItem';
import SegmentationPermission from '../../../../models/enums/segmentationPermission';
import NavigationWarning from '../../navigationWarning';
import { neutral } from '../../../../theme/colors';

interface Props extends RouteComponentProps {
  /** True is loading existing user */
  loading: boolean;
  /** An existing user to edit */
  user: User;
  /** True is creating a new user. False if editing an existing user. */
  isNewUser: boolean;
}

const ManageUserSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name is a required field.')
    .max(100, 'First name must be less than 1-100 characters'),
  lastName: Yup.string()
    .required('Last name is a required field.')
    .max(100, 'Last name must be less than 1-100 characters'),
  email: Yup.string().email('Must be valid email.').required('Email is a required field.')
});

const useStyles = makeStyles({
  userSwitchBoxContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  switchBoxContainer: {
    display: 'flex'
  },
  oktaStatus: {
    fontSize: 12,
    color: neutral[500]
  }
});

/**
 * @function UserManager Allows a user to view/create/edit a single user's information.
 */
const UserManager = ({ loading: loadingUser, user, isNewUser, history }: Props) => {
  const { createUser, loadingAssociatedData, clients, applications, fetchAssociatedData, editUser, fetchUser } =
    useUser();
  const { updateTag } = useTag();
  const classes = useStyles();

  const roles = [
    { value: 'User', disabled: false },
    { value: 'Client Admin', disabled: false },
    { value: 'Elite Admin', disabled: false }
  ];

  const [unassignedApps, setUnassignedApps] = React.useState<Application[]>(applications);
  const [assignedApps, setAssignedApps] = React.useState<Application[]>(user.assignedApps);
  const [unassignedClients, setUnassignedClients] = React.useState<(Client | AssignedClient)[]>([]);
  const [assignedClients, setAssignedClients] = React.useState<(Client | AssignedClient)[]>(user.assignedClients);

  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');

  const unblock = useRef(null);

  const formikRef = useRef(null);

  React.useEffect(() => {
    fetchAssociatedData();

    unblock.current = history.block((location) => {
      if (formikRef.current.dirty) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, []);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  React.useEffect(() => {
    if (isNewUser) {
      if (!loadingAssociatedData) {
        setUnassignedApps(applications);
        setUnassignedClients(clients);
      }
    } else {
      if (!loadingUser && !loadingAssociatedData) {
        if (user.assignedApps === null) {
          user.assignedApps = [];
        }
        if (user.assignedClients === null) {
          user.assignedClients = [];
        }
        setUnassignedApps(
          // filter out applications found in the user.assignedApps prop
          applications.filter(
            (app) =>
              user.assignedApps.findIndex((assignedApp) => app.entityDetailId === assignedApp.entityDetailId) === -1
          )
        );
        setAssignedApps(user.assignedApps);
        setUnassignedClients(
          clients.filter(
            (client) =>
              user.assignedClients.findIndex((assignedClient) => client.clientId === assignedClient.clientId) === -1
          )
        );
        setAssignedClients(user.assignedClients);
      }
    }
  }, [loadingUser, loadingAssociatedData]);

  const handleCancel = (dirty: boolean) => {
    history.push(Paths.Users);
  };

  if (loadingUser || loadingAssociatedData) {
    return (
      <div className="loadingIndicator">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      <h1>{isNewUser ? 'Add' : 'Edit'} User</h1>
      <Formik
        innerRef={formikRef}
        initialValues={user}
        validationSchema={ManageUserSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          try {
            const response = isNewUser ? await createUser(values) : await editUser(values);
            if (!response.success) {
              throw Error(`Failed to create user: ${response.message}`);
            }
            let tagUpdates = [];
            if (isNewUser) {
              // if new user, add all tags
              // must fetch new user to get the assigned id
              const newUser = await fetchUser(response.message);
              const tagUser: AssignedUser = {
                id: newUser.id,
                firstName: newUser.firstName,
                lastName: newUser.lastName,
                fullName: `${newUser.lastName}, ${newUser.firstName}`
              };
              tagUpdates = values.assignedTags.map((tag) =>
                updateTag(tag.clientId, tag.tagId, { ...tag, assignedUsers: [...tag.assignedUsers, tagUser] })
              );
            } else {
              // if edited user, remove user from previous tags and add to newly assigned ones
              tagUpdates.push(
                ...user.assignedTags
                  .filter(
                    (userTag) =>
                      values.assignedTags.findIndex((assignedTag) => assignedTag.tagId === userTag.tagId) === -1
                  )
                  .map((removedTag) =>
                    updateTag(removedTag.clientId, removedTag.tagId, {
                      ...removedTag,
                      assignedUsers: removedTag.assignedUsers.filter((assignedUser) => assignedUser.id !== user.id)
                    })
                  )
              );
              // add the user to any newly assigned tags
              const tagUser: AssignedUser = {
                id: values.id,
                firstName: values.firstName,
                lastName: values.lastName,
                fullName: `${values.lastName}, ${values.firstName}`
              };
              tagUpdates.push(
                ...values.assignedTags
                  .filter((tag) => user.assignedTags.findIndex((userTag) => userTag.tagId === tag.tagId) === -1)
                  .map((addedTag) =>
                    updateTag(addedTag.clientId, addedTag.tagId, {
                      ...addedTag,
                      assignedUsers: [...addedTag.assignedUsers, tagUser]
                    })
                  )
              );
            }
            await Promise.all(tagUpdates);
            setSubmitting(false);
            unblock.current();
            history.push(Paths.Users);
          } catch (err) {
            if (err instanceof Error) {
              setErrorMessage(err.message);
            } else {
              setErrorMessage(err.responseJSON.message);
            }
            window.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth'
            });
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, dirty }) => (
          <form>
            <FormikConsumer>
              {(formik) => (
                <NavigationWarning
                  formik={formik}
                  displayDirtyWarning={displayDirtyWarning}
                  confirmNavigationWarning={() => {
                    setConfirmedNavigation(true);
                    history.push(blockedLocation);
                  }}
                  cancleNavigationWarning={() => setDisplayDirtyWarning(false)}
                />
              )}
            </FormikConsumer>
            {isSubmitting && (
              <div className={commonStyles.overlay}>
                <div className={commonStyles.fixedCircularProgress}>
                  <CircularProgress />
                </div>
              </div>
            )}
            <fieldset className={commonStyles.fieldset}>
              <h5>Details</h5>
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.firstName || ''}
                  error={errors.firstName && touched.firstName}
                  helperText={errors.firstName && touched.firstName && errors.firstName}
                  required
                />
                <OutlinedInput
                  id="lastName"
                  label="Last Name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lastName || ''}
                  error={errors.lastName && touched.lastName}
                  helperText={errors.lastName && touched.lastName && errors.lastName}
                  required
                />
              </div>
              <OutlinedInput
                id="email"
                label="Email Address"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email || ''}
                error={errors.email && touched.email}
                helperText={errors.email && touched.email && errors.email}
                disabled={!isNewUser}
                required
              />
              <div className={commonStyles.twoColumn}>
                <OutlinedInput
                  id="title"
                  label="Title"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.title || ''}
                  error={errors.title && touched.title}
                  helperText={errors.title && touched.title && errors.title}
                />
                <OutlinedInput
                  id="department"
                  label="Company"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.department || ''}
                  error={errors.department && touched.department}
                  helperText={errors.department && touched.department && errors.department}
                />
              </div>
            </fieldset>
            <fieldset className={commonStyles.fieldset}>
              <h5>Settings</h5>
              <div className={classes.userSwitchBoxContainer}>
                <div className={classes.switchBoxContainer}>
                  <SwitchBox
                    id="activate"
                    value={values.activate}
                    handleChange={handleChange}
                    label="ACTIVE"
                    disabled={!UserOktaStatus[values.status]}
                  />
                  <SwitchBox
                    id="enhancedData"
                    value={values.enhancedData}
                    handleChange={handleChange}
                    label="Enhanced Data"
                  />
                  <SwitchBox
                    id="isFederated"
                    value={values.isFederated}
                    handleChange={handleChange}
                    label="Federated User"
                    disabled={!isNewUser}
                  />
                </div>
                <span className={classes.oktaStatus}>Current Status: {UserOktaStatus[values.status] ?? 'Unknown'}</span>
              </div>
              <div style={{ marginTop: '10px' }}>
                <RadioForm
                  key="role"
                  values={roles}
                  options={roles.map((role) => role.value)}
                  title={'User Type'}
                  selectedValue={user.role}
                  handleChange={(val) => {
                    setFieldValue('role', val);
                  }}
                />
              </div>
            </fieldset>
            <fieldset className={`${commonStyles.fieldset}`}>
              <h5>Apps</h5>
              <TransferList
                idField="entityId"
                labelField="name"
                left={unassignedApps}
                right={assignedApps}
                setLeft={setUnassignedApps}
                setRight={setAssignedApps}
                setFieldValue={setFieldValue}
                setField="assignedApps"
              />
            </fieldset>
            <fieldset className={`${commonStyles.fieldset}`}>
              <h5>Clients and Client Permissions</h5>
              <TransferList<Client | AssignedClient>
                idField="clientId"
                labelField="name"
                left={unassignedClients}
                right={assignedClients}
                setLeft={setUnassignedClients}
                setRight={setAssignedClients}
                setFieldValue={setFieldValue}
                setField="assignedClients"
                rightListComponent={ClientListItem}
                onMoveLeft={(clientsToRemove) => {
                  // remove assigned tags belonging to the removed clients
                  setFieldValue(
                    'assignedTags',
                    values.assignedTags.filter(
                      (assignedTag) =>
                        clientsToRemove.findIndex((client) => client.clientId == assignedTag.clientId) === -1
                    )
                  );
                  // filter out assigned fs permissions whose client id is found in the clients to remove
                  setFieldValue(
                    'assignedSegmentation',
                    values.assignedSegmentation.filter(
                      (assignedFS) =>
                        clientsToRemove.findIndex((client) => client.clientId === assignedFS.clientId) === -1
                    )
                  );
                }}
                onMoveRight={(clientsToAdd) =>
                  // add newly assigned clients fan segmentation permission objects
                  setFieldValue('assignedSegmentation', [
                    ...values.assignedSegmentation,
                    ...clientsToAdd.map((client) => ({
                      clientId: client.clientId,
                      segmentationPermission: SegmentationPermission.None
                    }))
                  ])
                }
              />
            </fieldset>
            <div className={commonStyles.formActions}>
              <TertiaryButton
                text="Cancel"
                title="Cancel"
                handleClick={() => {
                  handleCancel(dirty);
                }}
              />
              <span className={commonStyles.submitButton}>
                <PrimaryButton text="Save" title="Save" deactive={isSubmitting} handleClick={handleSubmit} />
              </span>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
export default withRouter(UserManager);
